"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, SunMedium, Briefcase, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Corporate Midweek vs. Weekend Drop",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Salt Lake Sector V, Rajarhat, and central commercial corridors experience heavy Monday-to-Thursday business occupancy with sharp weekend dips. We implement dual-segment pricing and dynamic stay restrictions to drive weekend staycations and social events.",
  metricLabel: "Strategy focus",
  metricValue: "Midweek corporate yield & weekend leisure package fences"
}, {
  id: 2,
  title: "Aggressive Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Monitor intraday price fluctuations across MakeMyTrip, Agoda, and Booking.com for competing Kolkata properties. Automated competitive tracking and dynamic pricing protect RevPAR without triggering margin-eroding discount wars.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA surveillance & rule-based rate positioning"
}, {
  id: 3,
  title: "OTA Dependency & Commission Leakage",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Eliminate unauthorized B2B rate leakage and curb excessive OTA commission outlays. We enforce tight rate parity across metasearch engines while boosting direct website bookings through high-converting incentives.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler parity control & direct booking engine boost"
}];
const seasonalityCycles = [{
  season: "Peak Festive & Winter (Oct – Feb)",
  badge: "High Demand / Max ADR",
  icon: Sparkles,
  focus: "Durga Puja, winter cultural festivals, NRI visits, and peak wedding dates.",
  tactic: "Strict MLOS (minimum length of stay), advance booking curves, and non-refundable premium room tiers."
}, {
  season: "Corporate & Business Season (Year-Round Midweek)",
  badge: "Steady Corporate Volume",
  icon: Briefcase,
  focus: "IT corridors in Sector V, New Town business hubs, and industrial trade delegations.",
  tactic: "Dynamic corporate bar rates, contracted volume yield management, and midweek length-of-stay controls."
}, {
  season: "Summer & Monsoon Shoulder (Apr – Aug)",
  badge: "Yield & Base Occupancy",
  icon: SunMedium,
  focus: "Medical tourism near EM Bypass, domestic transit stays, and local social banquets.",
  tactic: "Value-add packaging, medical transit partner rates, and proactive corporate MICE yielding."
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapekolkata" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Kolkata
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Kolkata presents a multifaceted hospitality micro-market combining steady IT and corporate demand in Salt Lake Sector V and New Town with high-impact festive surges during Durga Puja and the winter cultural season. Sunil Singh crafts precise, data-driven revenue strategies tailored for city business hotels, boutique heritage properties, and transit retreats.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality & Demand Rhythm Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.15
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="mt-14 md:mt-18 rounded-lg border border-border bg-card/60 p-6 sm:p-8 md:p-10 shadow-sm">
          <div className="max-w-2xl mb-8">
            <Badge variant="outline" className="mb-2 text-xs border-secondary/30 text-secondary">
              Seasonality Blueprint
            </Badge>
            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground">
              Kolkata Seasonality & Demand Cycles
            </h3>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground">
              Strategic pace-based pricing matched to Eastern India’s major economic and cultural calendar beats.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {seasonalityCycles.map((cycle, i) => {
            const IconComp = cycle.icon;
            return <div key={cycle.season} data-index={i} className="flex flex-col justify-between rounded-md border border-border/80 bg-background/80 p-5 space-y-4">
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <IconComp className="size-5 text-secondary" />
                      <span className="text-[11px] font-mono font-semibold uppercase tracking-wider text-secondary px-2 py-0.5 rounded bg-secondary/10">
                        {cycle.badge}
                      </span>
                    </div>
                    <h4 className="font-semibold text-foreground text-base">
                      {cycle.season}
                    </h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {cycle.focus}
                    </p>
                  </div>
                  <div className="pt-3 border-t border-border/60 text-xs text-foreground/90">
                    <span className="font-medium text-foreground block mb-0.5">Revenue Approach:</span>
                    <span className="text-muted-foreground">{cycle.tactic}</span>
                  </div>
                </div>;
          })}
          </div>
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Kolkata hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}